import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, rp, today } from "@/lib/api";
import { PageHeader, Card, Field, Sel } from "@/components/common";
import { LineEditor, blankLine, toPayload } from "@/components/LineEditor";
import { useAuth } from "@/context/AuthContext";

export default function PurchaseForm() {
  const { meta, myLocs, myAccounts, itemById, loadMeta, can } = useAuth();
  const methods = meta.payment_methods.filter((m) => m.active !== false && (m.type === "debt" || can("*") || myAccounts.some((a) => a.id === m.cash_account_id)));
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ date: today(), supplier_id: "", payment_method_id: methods[0]?.id || "", invoice_no: "", due_date: "", destination_id: myLocs[0]?.id || "", notes: "", request_id: null });
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    const rid = sp.get("request");
    if (!rid) return;
    api.get("/purchase-requests").then(({ data }) => {
      const pr = data.find((x) => x.id === rid);
      if (!pr) return;
      const it = itemById[pr.item_id];
      setF((x) => ({ ...x, destination_id: pr.location_id, request_id: pr.id, supplier_id: it?.supplier_id || "", notes: `Dari permintaan ${pr.code}` }));
      setLines([{ item_id: pr.item_id, qty: pr.qty, unit: pr.unit, unit_price: "" }]);
    });
  }, [sp, itemById]);

  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
  const pm = methods.find((m) => m.id === f.payment_method_id);
  const acc = meta.cash_accounts.find((a) => a.id === pm?.cash_account_id);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/purchases", { ...f, due_date: f.due_date || null, lines: toPayload(lines, true) });
      toast.success(`${data.code} dibuat · menunggu penerimaan`);
      loadMeta();
      nav(`/purchases/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-4xl">
      <PageHeader title="Pembelian baru" sub="Saat disimpan, pembelian dianggap disetujui: saldo kas berkurang dan barang masuk antrian penerimaan." />
      <Card className="p-4 space-y-4">
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="Tanggal"><input type="date" className="inp" value={f.date} onChange={(e) => set("date")(e.target.value)} data-testid="purchase-date-input" /></Field>
          <Field label="Supplier"><Sel testid="purchase-supplier-select" value={f.supplier_id} onChange={set("supplier_id")} placeholder="Pilih supplier" options={meta.suppliers.filter((s) => s.active !== false).map((s) => ({ value: s.id, label: s.name }))} /></Field>
          <Field label="Metode bayar"><Sel testid="purchase-payment-select" value={f.payment_method_id} onChange={set("payment_method_id")} placeholder="Pilih metode" options={methods.map((m) => ({ value: m.id, label: `${m.name}${m.type === "debt" ? " (hutang)" : ""}` }))} /></Field>
          <Field label="Tujuan"><Sel testid="purchase-destination-select" value={f.destination_id} onChange={set("destination_id")} options={myLocs.map((l) => ({ value: l.id, label: l.name }))} /></Field>
        </div>
        <Field label="Barang"><LineEditor lines={lines} setLines={setLines} priced testid="purchase-line" /></Field>
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="No. invoice / nota"><input className="inp" value={f.invoice_no} onChange={(e) => set("invoice_no")(e.target.value)} data-testid="purchase-invoice-input" /></Field>
          {pm?.type === "debt" && <Field label="Jatuh tempo"><input type="date" className="inp" value={f.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="purchase-due-input" /></Field>}
          <Field label="Catatan" className={pm?.type === "debt" ? "md:col-span-2" : "md:col-span-3"}><input className="inp" value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="purchase-notes-input" /></Field>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t pt-4">
          <div className="text-sm">Total: <span className="num text-xl font-bold" data-testid="purchase-total">{rp(total)}</span>
            {pm?.type === "debt" ? <span className="text-xs text-amber-700 ml-2 font-semibold">Dicatat sebagai hutang ke supplier (kas tidak berkurang)</span>
              : acc && <span className="text-xs text-slate-500 ml-2">Saldo {acc.name} setelah: <span className="num">{rp(acc.balance - total)}</span></span>}</div>
          <button disabled={busy} onClick={submit} className="h-10 px-5 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="purchase-submit-button">Simpan pembelian</button>
        </div>
      </Card>
    </div>
  );
}
